/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { SearchExpand } from '@repo/shared-client/components/blocks'
import {
  NativeSelectFormField,
  SwitchFormField,
  TagMultiSelectFormField,
  tagMultiSelectFromTags,
} from '@repo/shared-client/components/fields'
import { Button, Card, CardContent } from '@repo/shared-client/components/ui'
import { Form, useFormContext } from '@repo/shared-client/form'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import { useTranslation } from '@web/src/lib/intl/react'
import { FunnelPlusIcon, RotateCcwIcon, TagIcon } from 'lucide-react'
import { type ReactNode, useCallback } from 'react'

import type { PublicationListFilterFormValues } from '../publication-list-filter-types'
import type { PublicationListSideNavProps } from '../publication-list-side-nav/publication-list-side-nav'
import { usePublicationListFilterFields, usePublicationListFilterForm } from '../use-publication-list-filter-form'

export type PublicationListTopNavProps = PublicationListSideNavProps & {
  rightBarChildren?: ReactNode
}

function PublicationListTopNavFields({
  tags,
  isAuthenticated,
  hasPermission,
  filterResetValues,
  onFiltersApply,
  onReset,
  rightBarChildren,
}: Omit<PublicationListTopNavProps, 'filterDefaults'>) {
  const { t } = useTranslation()
  const { reset } = useFormContext<PublicationListFilterFormValues>()
  const { triggerFilterChange, handleSearchChange } = usePublicationListFilterFields(onFiltersApply)

  const handleResetFilters = useCallback(() => {
    reset(filterResetValues)
    onReset()
  }, [filterResetValues, onReset, reset])

  const filtersDisabled = !isAuthenticated

  return (
    <div className="flex w-full flex-col items-stretch gap-2">
      <SearchExpand
        clearSearchLabel={t('Clear search')}
        filterControlsDisabled={filtersDisabled}
        inputProps={{ 'aria-label': t('Search publications') }}
        minChars={0}
        name="search"
        onSearchChange={handleSearchChange}
        placeholder={t('Search by title or author...')}
        rightBarChildren={rightBarChildren}
        toggleFiltersLabel={t('Toggle filters')}
      >
        <Card>
          <CardContent className="flex flex-col gap-4">
            <NativeSelectFormField
              disabled={filtersDisabled}
              label={t('Sort By')}
              name="sortBy"
              onValueChange={() => triggerFilterChange()}
            >
              <option value="AUTHOR">{t('Author')}</option>
              <option value="LATEST">{t('Latest')}</option>
              <option value="RATING">{t('Rating')}</option>
              <option value="SERIES">{t('Series')}</option>
              <option value="TITLE">{t('Title')}</option>
            </NativeSelectFormField>

            <div className="flex items-center gap-2">
              <TagIcon size={16} />
              <span className="font-medium text-foreground text-sm">{t('Tag Filter')}</span>
            </div>
            <TagMultiSelectFormField
              disabled={filtersDisabled}
              name="tags"
              onValueChange={() => triggerFilterChange()}
              onValueChangeTrigger="close"
              options={tagMultiSelectFromTags(tags)}
              placeholder={t('All')}
            />

            <div className="flex items-center gap-2">
              <FunnelPlusIcon size={16} />
              <span className="font-medium text-foreground text-sm">{t('Other Filters')}</span>
            </div>
            <div className="flex flex-col items-stretch gap-3 py-1">
              {isAuthenticated && (
                <SwitchFormField
                  disabled={filtersDisabled}
                  label={t('Cliterature I Maintain')}
                  name="myPublications"
                  onCheckedChange={(checked) => triggerFilterChange({ myPublications: checked })}
                />
              )}

              {hasPermission('publication:review') && (
                <SwitchFormField
                  disabled={filtersDisabled}
                  label={t('Needs Review')}
                  name="needsReview"
                  onCheckedChange={(checked) => triggerFilterChange({ needsReview: checked })}
                />
              )}
            </div>

            {isAuthenticated && (
              <Button
                className="w-full text-muted-foreground"
                onClick={handleResetFilters}
                size="sm"
                type="button"
                variant="ghost"
              >
                <RotateCcwIcon size={14} />
                {t('Reset Filters')}
              </Button>
            )}
            {!isAuthenticated && <SFJoinButton className="w-full" size="sm" />}
          </CardContent>
        </Card>
      </SearchExpand>
    </div>
  )
}

export default function PublicationListTopNav({
  tags,
  isAuthenticated,
  hasPermission,
  filterDefaults,
  filterResetValues,
  onFiltersApply,
  onReset,
  rightBarChildren,
}: PublicationListTopNavProps) {
  const formMethods = usePublicationListFilterForm(filterDefaults)

  return (
    <Form formMethods={formMethods} suppressHydrationWarning>
      <PublicationListTopNavFields
        filterResetValues={filterResetValues}
        hasPermission={hasPermission}
        isAuthenticated={isAuthenticated}
        onFiltersApply={onFiltersApply}
        onReset={onReset}
        rightBarChildren={rightBarChildren}
        tags={tags}
      />
    </Form>
  )
}
